import React, { useEffect, useMemo, useState } from "react";
import styles from "./savior-simulator.module.css";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import StarContainer from "../../components/star-container/star-container";
import ScrollUpButton from "../../components/buttons/scroll-up-button/scroll-up-button";
import { useSelector } from "react-redux";
import FilterSelect from "../../components/filter-select/filter-select";
import Portrait from "../../components/savior/portrait/portrait";
import { selectSaviorEntities } from "../../store/selectors/saviorSelectors";
import ComparisonGroup from "../../components/savior-simulator/comparison-group";
import PresetChanger from "../../components/savior-simulator/preset-changer";
import {
    getArchivePresets,
    getCharPresets,
    getEquipPresets,
    getTactics,
    updateArchivePreset,
    updateCharPreset,
    updateEquipPreset,
    updateTactics,
} from "../../indexedDB/presets";
import EquipSelectorModal from "../../components/savior-simulator/modal/equip-selector-modal";
import StatCollection from "../../components/savior-simulator/stat-collection";
import TacticsSelectorModal from "../../components/savior-simulator/modal/tactics-selector-modal";
import PresetExportModal from "../../components/savior-simulator/modal/preset-export-modal";
import getImageFileName from "../../functions/getImageFileName";
import ExtractImageTemplet from "../../components/savior-simulator/extract-image-templet";
import { createRoot } from "react-dom/client";
import { toPng } from "html-to-image";
import { getUIText } from "../../translation";
import { EQUIP_PART_TYPE, LOCALES, NORMAL_STATUS_TYPE, SAVIOR_ATTRIBUTE, SAVIOR_CLASS, SKILL_TYPE } from "../../constants";
import Decimal from "decimal.js";
import { loadData } from "../../functions/dataLoader";
import { getSynchroBonus } from "../../functions/getSynchroFunctions";
import { selectArcanaEntities } from "../../store/selectors/arcanaSelectors";
import SearchBox from "../../components/search-box/search-box";
import { choSearch, registQuery } from "../../functions/searchFunctions";

const STAT_TYPE = {
    atk: "공격력",
    hp: "생명력",
    def: "방어력",
    spd: "속도",
    crit_rate: "치명타 확률",
    crit_dmg: "치명타 대미지",
    eff_rate: "효과 적중",
    eff_resist: "효과 저항",
};

const STAT_TYPE_RESONANCE = {
    atk: "atk",
    atk_rate: "atk",
    hp: "hp",
    hp_rate: "hp",
    def: "def",
    def_rate: "def",
    spd: "spd",
    spd_rate: "spd",
    crit_rate: "crit_rate",
    crit_dmg: "crit_dmg",
    eff_rate: "eff_rate",
    eff_resist: "eff_resist",
};

const ARCHIVE_TO_NORMAL = {
    str: "atk",
    hp: "hp",
    end: "def",
    focus: "eff_rate",
    prot: "eff_resist",
};

const ARCHIVE_TO_NORMAL_RATE = {
    defender: {
        str: 0.8,
        hp: 1.1,
        end: 1.1,
        focus: 1,
        prot: 1,
    },
    striker: {
        str: 1.1,
        hp: 1,
        end: 0.9,
        focus: 1,
        prot: 1,
    },
    ranger: {
        str: 1.1,
        hp: 1,
        end: 0.9,
        focus: 1,
        prot: 1,
    },
    caster: {
        str: 1.2,
        hp: 0.9,
        end: 0.9,
        focus: 1,
        prot: 1,
    },
    assassin: {
        str: 1.2,
        hp: 0.9,
        end: 0.9,
        focus: 1,
        prot: 1,
    },
    supporter: {
        str: 0.8,
        hp: 1.1,
        end: 1.1,
        focus: 1,
        prot: 1,
    },
};

const canMultiplyStats = ["atk", "hp", "def", "spd"];
const onlySumStats = ["crit_rate", "crit_dmg", "eff_rate", "eff_resist"];

const DOT_DEBUFF_LIST = ["BI_BLEEDING", "BI_BURN", "BI_FREEZING"];

export default function SaviorSimulator() {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);

    const [saviorsOrigin, setSaviorsOrigin] = useState(null);
    const [potentialsOrigin, setPotentialsOrigin] = useState(null);
    const [setOrigin, setSetOrigin] = useState(null);
    const [tacticsOrigin, setTacticsOrigin] = useState(null);
    const [synchroLevelData, setSynchroLevelData] = useState(null);

    useEffect(() => {
        loadData("saviors.json").then(setSaviorsOrigin);
        loadData("potentials.json").then(setPotentialsOrigin);
        loadData("equipments_sets.json").then(setSetOrigin);
        loadData("tactics.json").then(setTacticsOrigin);
        loadData("synchro_level_data.json").then(setSynchroLevelData);
    }, []);

    if (!saviorsOrigin || !potentialsOrigin || !setOrigin || !tacticsOrigin || !synchroLevelData) {
        return (
            <div id="container" data-theme={theme} data-lang={lang}>
                {theme === "dark" && <StarContainer />}
                <Header title={getUIText(lang, "header.title.simulatorSavior")} />
                <div>{getUIText(lang, "monster.loading")}</div>
                <Footer />
            </div>
        );
    }

    return (
        <SaviorSimulatorInner
            saviorsOrigin={saviorsOrigin}
            potentialsOrigin={potentialsOrigin}
            setOrigin={setOrigin}
            tacticsOrigin={tacticsOrigin}
            synchroLevelData={synchroLevelData}
        />
    );
}

function SaviorSimulatorInner({ saviorsOrigin, potentialsOrigin, setOrigin, tacticsOrigin, synchroLevelData }) {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);

    const saviors = useSelector(selectSaviorEntities);
    const arcanas = useSelector(selectArcanaEntities);

    const charactorNameFilterList = useMemo(() => {
        return saviorsOrigin
            .map((e) => {
                return {
                    name: `${e.name[lang]}(${e.title[lang]})`,
                    img: `${e.name[LOCALES.KO]}(${e.title[LOCALES.KO]})`,
                    value: e.id,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, lang));
    }, [lang]);

    const potentialNameFilterList = useMemo(() => {
        const arcanaList = Object.values(arcanas);
        return [
            { name: getUIText(lang, "simulator.savior.archive.potential.title"), value: "잠재력 선택" },
            ...potentialsOrigin
                .filter((e) => e.name["ko-KR"] !== "클레어 고유 잠재력")
                .map((e) => {
                    const contained = arcanaList.filter((a) => a.spPotential?.name["ko-KR"] === e.name["ko-KR"]);
                    if (contained.length === 0) {
                        return {
                            name: e.name[lang],
                            value: e.id,
                        };
                    } else {
                        const elses = contained.length === 1 ? "" : ` 외 ${contained.length - 1}`;
                        return {
                            name: `${e.name[lang]} (${contained[0].name[lang]}-${contained[0].charName[lang]}${elses})`,
                            value: e.id,
                        };
                    }
                }),
        ];
    }, [lang]);

    const lastChar = useMemo(() => {
        const loadedRaw = localStorage.getItem("lastChar");
        let result = charactorNameFilterList[0];

        if (loadedRaw) {
            try {
                const loaded = JSON.parse(loadedRaw);
                const found = charactorNameFilterList.find((e) => e.value === loaded.value);

                result = found;
            } catch (e) {
                result = charactorNameFilterList[0];
            }
        }

        return result;
    }, []);

    const [selectedChar, setSelectedChar] = useState(lastChar);

    const [saviorPresets, setSaviorPresets] = useState([]);
    const [saviorPresetIndex, setSaviorPresetIndex] = useState(0);

    const [tactics, setTactics] = useState(null);

    const [archivePresets, setArchivePresets] = useState([]);
    const [archivePresetIndex, setArchivePresetIndex] = useState(0);

    const [equipPresets, setEquipPresets] = useState([]);
    const [equipPresetIndex, setEquipPresetIndex] = useState(0);

    const [editingEquip, setEditingEquip] = useState(null);
    const [editingTactics, setEditingTactics] = useState(null);
    const [equipIsFold, setEquipIsFold] = useState([true, true, true, true, true, true]);

    const [tacticsIsFold, setTacticsIsFold] = useState(true);

    // const [isActiveExportModal, setIsActiveExportModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const [arcanaQuery, setArcanaQuery] = useState("");
    const [resultList, setResultList] = useState([]);

    const totalList = useMemo(() => {
        const arcanaList = structuredClone(Object.values(arcanas));
        return [
            ...potentialsOrigin
                .filter((e) => e.name["ko-KR"] !== "클레어 고유 잠재력")
                .map((e) => {
                    return {
                        resultType: "potential",
                        ...e,
                        searchText: `${Object.values(e.name).join(" ")}`,
                    };
                }),
            ...arcanaList

                .map((e) => {
                    const rewards = [];
                    e.events.forEach((ev) =>
                        ev.choices.forEach((ch) => {
                            ch.success_rewards.forEach((r) =>
                                r.forEach((reward) => {
                                    if (reward.type === "RT_SE_POTEN") {
                                        const found = rewards.find((re) => re.reward_id === reward.reward_id);

                                        if (!found) rewards.push(reward);
                                    }
                                }),
                            );
                            if (ch.failure_rewards.length > 0) {
                                ch.failure_rewards.forEach((r) =>
                                    r.forEach((reward) => {
                                        if (reward.type === "RT_SE_POTEN") {
                                            const found = rewards.find((re) => re.reward_id === reward.reward_id);

                                            if (!found) rewards.push(reward);
                                        }
                                    }),
                                );
                            }
                        }),
                    );

                    return {
                        resultType: "arcana",
                        ...e,
                        searchText: `${Object.values(e.name).join(" ")} ${Object.values(e.charName).join(" ")}`,
                        potentials: rewards,
                    };
                })
                .filter((e) => {
                    if (e.potentials.length === 0) return false;

                    return true;
                }),
        ];
    }, []);

    useEffect(() => {
        const result = totalList.filter((e) => {
            if (arcanaQuery === "") return false;

            registQuery(lang, arcanaQuery);
            return choSearch(e.searchText);
        });

        setResultList(result);
    }, [arcanaQuery]);

    useEffect(() => {
        const found = charactorNameFilterList.find((e) => e.name === selectedChar.name && e.value === selectedChar.value);

        if (found) {
            localStorage.setItem("lastChar", JSON.stringify(found));
        }
    }, [selectedChar]);

    const totalSaviorStats = useMemo(() => {
        const result = {
            base: [],
            level: [],
            resonance: [],
            passive: [],
            limitBreak: 0,
            starGrade: [],
            favor: [],
            accSkillLevel: 0,
        };

        if (saviorPresets[archivePresetIndex]) {
            const preset = saviorPresets[archivePresetIndex];
            const baseSavior = saviors[selectedChar.value];

            Object.keys(baseSavior.base_stat).forEach((statType) => {
                result.base.push({
                    statType: statType,
                    isPercent: statType.includes("_") || statType === "acc" ? true : false,
                    value: baseSavior.base_stat[statType],
                });
            });

            const synchroBonus = getSynchroBonus(synchroLevelData, preset.level);

            result.level.push({
                statType: "atk",
                isPercent: false,
                value: baseSavior.stat_per_level.atk * (preset.level - 1) + synchroBonus.atk,
            });
            result.level.push({
                statType: "hp",
                isPercent: false,
                value: baseSavior.stat_per_level.hp * (preset.level - 1) + synchroBonus.hp,
            });
            result.level.push({
                statType: "def",
                isPercent: false,
                value: baseSavior.stat_per_level.def * (preset.level - 1) + synchroBonus.def,
            });

            baseSavior.resonances
                .filter((e) => e.step <= preset.resonance)
                .forEach((resonance) => {
                    resonance.stats.forEach((stat) => {
                        const statType = STAT_TYPE_RESONANCE[stat.type];
                        const isPercent = stat.type.includes("_") ? true : false;

                        let found = result.resonance.find((e) => e.statType === statType && e.isPercent === isPercent);
                        if (found) {
                            found.value += stat.value;
                        } else {
                            found = {
                                statType: statType,
                                isPercent: isPercent,
                                value: stat.value,
                            };

                            result.resonance.push(found);
                        }
                    });
                });

            const passive = baseSavior.skills[0].levels[preset.limitBreak]?.stat ? baseSavior.skills[0].levels[preset.limitBreak].stat : [];
            passive.forEach((stat) => {
                const statType = STAT_TYPE_RESONANCE[stat.type];
                const isPercent = stat.type.includes("_") ? true : false;

                let found = result.passive.find((e) => e.statType === statType && e.isPercent === isPercent);
                if (found) {
                    found.value += stat.value;
                } else {
                    found = {
                        statType: statType,
                        isPercent: isPercent,
                        value: stat.value,
                    };

                    result.passive.push(found);
                }
            });

            result.limitBreak = preset.limitBreak;

            if (preset.starGrade > 0) {
                result.level.push({ statType: "atk", isPercent: true, value: 0.02 * preset.starGrade });
                result.level.push({ statType: "hp", isPercent: true, value: 0.02 * preset.starGrade });
                result.level.push({ statType: "def", isPercent: true, value: 0.02 * preset.starGrade });
            }

            if (preset.favor > 1) {
                baseSavior.favor_per_level
                    .filter((e) => e.level <= preset.favor)
                    .forEach((favor) => {
                        favor.stats.forEach((stat) => {
                            let found = result.favor.find((e) => e.statType === stat.type && e.isPercent === false);

                            if (found) {
                                found.value += stat.value;
                            } else {
                                found = {
                                    statType: stat.type,
                                    isPercent: false,
                                    value: stat.value,
                                };

                                result.favor.push(found);
                            }
                        });
                    });
            }

            preset.skillLevels.forEach((level) => (result.accSkillLevel += level));
            result.accSkillLevel += preset.limitBreak;
        }

        return result;
    }, [saviorPresets, archivePresetIndex, synchroLevelData]);

    const totalTacticsStats = useMemo(() => {
        const result = [];

        if (tactics) {
            const savior = saviors[selectedChar.value];
            const saviorAttr = SAVIOR_ATTRIBUTE[savior.attr][LOCALES.EN].toLowerCase();
            const saviorClass = SAVIOR_CLASS[savior.class][LOCALES.EN].toLowerCase();

            tacticsOrigin.savior
                .filter((e) => e.level <= tactics.savior)
                .forEach((level) => {
                    level.stats.forEach((stat) => {
                        let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                        if (found) {
                            found.value += stat.value;
                        } else {
                            found = {
                                statType: stat.type,
                                isPercent: stat.is_percent,
                                value: stat.value,
                            };

                            result.push(found);
                        }
                    });
                });

            tacticsOrigin.arcana
                .filter((e) => e.level <= tactics.arcana.level)
                .forEach((level) => {
                    level.stats.forEach((stat) => {
                        let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                        if (found) {
                            found.value += stat.value;
                        } else {
                            found = {
                                statType: stat.type,
                                isPercent: stat.is_percent,
                                value: stat.value,
                            };

                            result.push(found);
                        }
                    });
                });
            if (tactics.arcana.lastType !== null) {
                const nextLevel = tacticsOrigin.arcana.find((e) => e.level === tactics.arcana.level + 1);
                const length = tactics.arcana.lastType === "atk" ? 1 : tactics.arcana.lastType === "def" ? 2 : 0;

                for (let i = 0; i < length; i++) {
                    const stat = nextLevel.stats[i];
                    let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                    if (found) {
                        found.value += stat.value;
                    } else {
                        found = {
                            statType: stat.type,
                            isPercent: stat.is_percent,
                            value: stat.value,
                        };

                        result.push(found);
                    }
                }
            }

            tacticsOrigin.attr[saviorAttr]
                .filter((e) => e.level <= tactics.attr[saviorAttr].level)
                .forEach((level) => {
                    level.stats.forEach((stat) => {
                        let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                        if (found) {
                            found.value += stat.value;
                        } else {
                            found = {
                                statType: stat.type,
                                isPercent: stat.is_percent,
                                value: stat.value,
                            };

                            result.push(found);
                        }
                    });
                });
            if (tactics.attr[saviorAttr].lastType !== null) {
                const nextLevel = tacticsOrigin.attr[saviorAttr].find((e) => e.level === tactics.attr[saviorAttr].level + 1);
                const length = tactics.attr[saviorAttr].lastType === "atk" ? 1 : tactics.attr[saviorAttr].lastType === "def" ? 2 : 0;

                for (let i = 0; i < length; i++) {
                    const stat = nextLevel.stats[i];
                    let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                    if (found) {
                        found.value += stat.value;
                    } else {
                        found = {
                            statType: stat.type,
                            isPercent: stat.is_percent,
                            value: stat.value,
                        };

                        result.push(found);
                    }
                }
            }

            tacticsOrigin.class[saviorClass]
                .filter((e) => e.level <= tactics.class[saviorClass].level)
                .forEach((level) => {
                    level.stats.forEach((stat) => {
                        let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                        if (found) {
                            found.value += stat.value;
                        } else {
                            found = {
                                statType: stat.type,
                                isPercent: stat.is_percent,
                                value: stat.value,
                            };

                            result.push(found);
                        }
                    });
                });
            if (tactics.class[saviorClass].lastType !== null) {
                const nextLevel = tacticsOrigin.class[saviorClass].find((e) => e.level === tactics.class[saviorClass].level + 1);
                const length = tactics.class[saviorClass].lastType === "atk" ? 1 : tactics.class[saviorClass].lastType === "def" ? 2 : 0;

                for (let i = 0; i < length; i++) {
                    const stat = nextLevel.stats[i];
                    let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                    if (found) {
                        found.value += stat.value;
                    } else {
                        found = {
                            statType: stat.type,
                            isPercent: stat.is_percent,
                            value: stat.value,
                        };

                        result.push(found);
                    }
                }
            }

            const facilityStats = tacticsOrigin.facility[tactics.facility - 1];
            facilityStats.forEach((stat) => {
                let found = result.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                if (found) {
                    found.value += stat.value;
                } else {
                    found = {
                        statType: stat.type,
                        isPercent: stat.is_percent,
                        value: stat.value,
                    };

                    result.push(found);
                }
            });
        }

        return result;
    }, [tactics]);

    const totalArchiveStats = useMemo(() => {
        const result = {
            stats: [],
            potentialStats: [],
            activePotentials: [],
        };

        if (archivePresets[archivePresetIndex]) {
            const preset = archivePresets[archivePresetIndex];
            const savior = saviors[selectedChar.value];

            Object.keys(preset.stat).forEach((jstat) => {
                const statType = ARCHIVE_TO_NORMAL[jstat];
                const isPercent = statType.includes("_");
                const saviorClass = SAVIOR_CLASS[savior.class][LOCALES.EN].toLowerCase();
                let value = preset.stat[jstat] * ARCHIVE_TO_NORMAL_RATE[saviorClass][jstat];

                if (statType === "hp") value *= 6;
                else if (statType === "def") value *= 0.5;
                else if (statType === "eff_rate" || statType === "eff_resist") value *= 4;

                result.stats.push({
                    statType: statType,
                    isPercent: isPercent,
                    value: Math.floor(value),
                });
            });

            preset.potentials.forEach((p) => {
                const potential = potentialsOrigin.find((e) => e.id === p.id);
                const level = potential.levels[p.level - 1];
                // console.log(potential);
                level.stats.forEach((stat) => {
                    let found = result.potentialStats.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                    if (found) {
                        found.value += stat.value;
                    } else {
                        found = {
                            statType: stat.type,
                            isPercent: stat.is_percent,
                            value: stat.value,
                        };

                        result.potentialStats.push(found);
                    }
                });

                // if (level.se_list.length > 0) {
                //     console.log(p);
                // }
                result.activePotentials.push(p);
            });
            // console.log(result.activePotentials);
        }

        return result;
    }, [archivePresets, archivePresetIndex]);

    const totalEquipStats = useMemo(() => {
        const result = {
            slot: 0,
            options: [],
            sets: [],
        };

        if (!equipPresets[equipPresetIndex] || equipPresets[equipPresetIndex].slot === 0) return result;

        if (equipPresets[equipPresetIndex]) {
            result.slot = equipPresets[equipPresetIndex].slot;

            equipPresets[equipPresetIndex].equips.forEach((equip) => {
                let found = result.options.find(
                    (e) => e.statType === equip.mainOption.statType && e.isPercent === equip.mainOption.isPercent,
                );

                let value = equip.mainOption.baseValue + equip.mainOption.gap * (equip.enhanced + (equip.isEngraved ? 3 : 0));
                if (found) {
                    found.value += value;
                } else {
                    found = {
                        statType: equip.mainOption.statType,
                        isPercent: equip.mainOption.isPercent,
                        value: value,
                    };
                    result.options.push(found);
                }

                equip.subOptions.forEach((option) => {
                    found = result.options.find((e) => e.statType === option.statType && e.isPercent === option.isPercent);
                    if (found) {
                        found.value += option.value;
                    } else {
                        found = {
                            statType: option.statType,
                            isPercent: option.isPercent,
                            value: option.value,
                        };
                        result.options.push(found);
                    }
                });

                found = result.sets.find((e) => e.id === equip.setId && e.parts !== e.need_parts);
                if (found) {
                    if (++found.parts === found.need_parts) {
                        found.stats.forEach((stat) => {
                            let temp = result.options.find((e) => e.statType === stat.type && e.isPercent === stat.is_percent);

                            if (temp) {
                                temp.value += stat.value;
                            } else {
                                temp = {
                                    statType: stat.type,
                                    isPercent: stat.is_percent,
                                    value: stat.value,
                                };
                                result.options.push(temp);
                            }
                        });
                    }
                } else {
                    found = structuredClone(setOrigin.find((e) => e.id === equip.setId));
                    found.parts = 1;

                    result.sets.push(found);
                }
            });
        }

        return result;
    }, [equipPresets, equipPresetIndex]);

    const statResult = useMemo(() => {
        const result = {
            atk: 0,
            hp: 0,
            def: 0,
            spd: 0,
            crit_rate: 0,
            crit_dmg: 0,
            eff_rate: 0,
            eff_resist: 0,
            battle_power: 0,
            battle_power_defense: 0,
            battle_power_balance: 0,
        };

        canMultiplyStats.forEach((statName) => {
            const base_int = totalSaviorStats.base.find((e) => e.statType === statName && e.isPercent === false);
            const level_int = totalSaviorStats.level.find((e) => e.statType === statName && e.isPercent === false);
            const archive_int = totalArchiveStats.stats.find((e) => e.statType === statName && e.isPercent === false);

            const A = (base_int ? base_int.value : 0) + (level_int ? level_int.value : 0) + (archive_int ? archive_int.value : 0);

            const potential_int = totalArchiveStats.potentialStats.find((e) => e.statType === statName && e.isPercent === false);
            const tactics_int = totalTacticsStats.find((e) => e.statType === statName && e.isPercent === false);
            const favor_int = totalSaviorStats.favor.find((e) => e.statType === statName && e.isPercent === false);
            const equip_int = totalEquipStats.options.find((e) => e.statType === statName && e.isPercent === false);

            const B =
                (potential_int ? potential_int.value : 0) +
                (tactics_int ? tactics_int.value : 0) +
                (favor_int ? favor_int.value : 0) +
                (equip_int ? equip_int.value : 0);

            const resonance_int = totalSaviorStats.resonance.find((e) => e.statType === statName && e.isPercent === false);
            const resonance_per = totalSaviorStats.resonance.find((e) => e.statType === statName && e.isPercent === true);

            const C = resonance_per ? resonance_per.value : 0;

            const passive_per = totalSaviorStats.passive.find((e) => e.statType === statName && e.isPercent === true);
            const potential_per = totalArchiveStats.potentialStats.find((e) => e.statType === statName && e.isPercent === true);
            const tactics_per = totalTacticsStats.find((e) => e.statType === statName && e.isPercent === true);
            const starGrade_per = totalSaviorStats.starGrade.find((e) => e.statType === statName && e.isPercent === true);
            const equip_per = totalEquipStats.options.find((e) => e.statType === statName && e.isPercent === true);

            const D =
                (passive_per ? passive_per.value : 0) +
                (potential_per ? potential_per.value : 0) +
                (tactics_per ? tactics_per.value : 0) +
                (starGrade_per ? starGrade_per.value : 0) +
                (equip_per ? equip_per.value : 0);

            result[statName] = Math.floor((A * (1 + C) + (resonance_int ? resonance_int.value : 0)) * (1 + D / 10_000) + B);
        });

        onlySumStats.forEach((statName) => {
            const base_per = totalSaviorStats.base.find((e) => e.statType === statName && e.isPercent === true);
            const resonance_per = totalSaviorStats.resonance.find((e) => e.statType === statName && e.isPercent === true);
            const passive_per = totalSaviorStats.passive.find((e) => e.statType === statName && e.isPercent === true);
            const potential_per = totalArchiveStats.potentialStats.find((e) => e.statType === statName && e.isPercent === true);
            const equip_per = totalEquipStats.options.find((e) => e.statType === statName && e.isPercent === true);
            const archive_per = totalArchiveStats.stats.find((e) => e.statType === statName && e.isPercent === true);

            const optional = (statName === "eff_rate" || statName === "eff_resist") && archive_per ? archive_per.value : 0;

            result[statName] =
                ((base_per ? base_per.value * 10_000 : 0) +
                    (resonance_per ? resonance_per.value * 10_000 : 0) +
                    (passive_per ? passive_per.value : 0) +
                    (potential_per ? potential_per.value : 0) +
                    (equip_per ? equip_per.value : 0) +
                    optional) /
                1;
        });

        const atkBase = new Decimal(result.atk).div(10).mul(12).toNumber();
        const finalCritRate = Math.min(new Decimal(result.crit_rate).div(10_000).toNumber(), 1);
        const finalCritDmg = new Decimal(result.crit_dmg).div(10_000).toNumber();
        const nonCritDmg = new Decimal(atkBase).mul(1 - finalCritRate).toNumber();
        const critDmg = new Decimal(atkBase).mul(finalCritRate).mul(new Decimal(finalCritDmg).plus(1).toNumber()).toNumber();
        const atkFinal = new Decimal(nonCritDmg).plus(critDmg).toNumber();
        const hpBase = new Decimal(result.hp).div(55).mul(12).toNumber();
        const defBase = new Decimal(result.def).div(5).mul(12).toNumber();
        const spdWeight = new Decimal(result.spd).minus(100).div(5).plus(100).div(100).toNumber();
        const effWeight =
            1 +
            new Decimal(result.eff_rate)
                .div(10_000)
                .minus(1)
                .plus(new Decimal(result.eff_resist).div(10_000).toNumber())
                .div(10)
                .toNumber();

        // 잠재력 레벨 중 bp_check(전투력 포함 여부)가 true인 것만 합산 — dll의 PotenBattlePowerCheck 플래그
        let potentialWeight = 0;
        totalArchiveStats.activePotentials.forEach((p) => {
            const potential = potentialsOrigin.find((e) => e.id === p.id);
            for (let i = 0; i < p.level; i++) {
                if (potential.levels[i].bp_check) {
                    potentialWeight += potential.levels[i].req_pp;
                }
            }
        });
        potentialWeight /= 10_000;

        // 한계돌파 팩터는 캐릭터별 한계돌파 스탯이 아니라 dll에 고정 상수(3)로 박혀 있음 — 실측 검증 완료(샤를/에밀리)
        const finalWeight = (3 * 200 + totalSaviorStats.accSkillLevel * 100) / 10_000;

        // 공격 위주 전투력 (dll: DEFAULT) — 공격력만 치명타로 분배
        result.battle_power = Math.floor((atkFinal + hpBase + defBase) * spdWeight * effWeight * (1 + potentialWeight) * (1 + finalWeight));

        // 방어 위주 전투력 (dll: BALANCE) — 공/생/방 평균을 치명타로 분배하고, 생명력/방어력은 별도로 한 번 더 더함
        const balanceBase = new Decimal(atkBase).plus(hpBase).plus(defBase).div(3).toNumber();
        const balanceNonCritDmg = new Decimal(balanceBase).mul(1 - finalCritRate).toNumber();
        const balanceCritDmg = new Decimal(balanceBase).mul(finalCritRate).mul(new Decimal(finalCritDmg).plus(1).toNumber()).toNumber();
        const balanceFinal = new Decimal(balanceNonCritDmg).plus(balanceCritDmg).toNumber();
        result.battle_power_defense = Math.floor(
            (balanceFinal + hpBase + defBase) * spdWeight * effWeight * (1 + potentialWeight) * (1 + finalWeight),
        );

        // 밸런스 위주 전투력 (dll: CRIT_DEFAULT) — 공/생/방은 치명타 미반영, 치명타는 별도 가산항으로 곱함
        const critFactor = new Decimal(finalCritRate).plus(new Decimal(finalCritDmg).div(2).toNumber()).div(3).toNumber();
        result.battle_power_balance = Math.floor(
            (atkBase + hpBase + defBase) * (1 + critFactor) * spdWeight * effWeight * (1 + potentialWeight) * (1 + finalWeight),
        );
        result.activePotentials = totalArchiveStats.activePotentials.filter((poten) => {
            const potential = potentialsOrigin.find((e) => e.id === poten.id);

            return (
                potential.levels[poten.level - 1].se_list.filter(
                    (e) =>
                        e.healStatType !== null ||
                        e.barrierStatType !== null ||
                        e.addDamageStatType !== null ||
                        DOT_DEBUFF_LIST.includes(e.buff_str_id),
                ).length > 0
            );
        });

        return result;
    }, [totalSaviorStats, totalTacticsStats, totalArchiveStats, totalEquipStats]);

    const loadPresets = async () => {
        const saviorPresets = await getCharPresets(lang, selectedChar.value, saviors[selectedChar.value].name);
        const archivePresets = await getArchivePresets(lang, saviors[selectedChar.value]);
        const equipPresets = await getEquipPresets(lang, selectedChar.value, saviors[selectedChar.value].name);
        const tactics = await getTactics();

        setSaviorPresets(saviorPresets);
        setArchivePresets(archivePresets);
        setEquipPresets(equipPresets);
        setTactics(tactics);
    };

    useEffect(() => {
        loadPresets();
    }, []);

    useEffect(() => {
        loadPresets();
    }, [selectedChar]);

    const setPresetName = (type, name) => {
        let setter;
        let updateFunc;
        let presetIndex;

        if (type === "savior") {
            setter = setSaviorPresets;
            updateFunc = updateCharPreset;
            presetIndex = saviorPresetIndex;
        } else if (type === "archive") {
            setter = setArchivePresets;
            updateFunc = updateCharPreset;
            presetIndex = archivePresetIndex;
        } else if (type === "equip") {
            setter = setEquipPresets;
            updateFunc = updateCharPreset;
            presetIndex = equipPresetIndex;
        }

        setter((prev) => {
            const updated = structuredClone(prev);

            updated.forEach((preset, index) => {
                if (index === presetIndex) {
                    preset.name = name;

                    updateFunc(preset);
                }
            });

            return updated;
        });
    };

    const setVerifiedSaviorValue = (flag, textValue) => {
        let value = Number(textValue);
        if (Number.isNaN(value)) {
            return;
        }

        value = Math.floor(value);

        if (flag === "level" && value > 601) {
            value = 600;
        } else if (flag === "resonance" && value > 10) {
            value = 10;
        } else if (flag === "limitBreak" && value > 3) {
            value = 3;
        } else if (flag === "starGrade" && value > 7) {
            value = 7;
        } else if (flag === "favor" && value > 60) {
            value = 60;
        }

        setSaviorPresets((prev) => {
            const updated = prev.map((p) => {
                if (p.slot === saviorPresets[saviorPresetIndex].slot) {
                    p[flag] = value;
                    return { ...p };
                }
                return p;
            });

            return updated;
        });
    };

    const setVerifiedSkillValue = (skillIndex, textValue) => {
        let value = Number(textValue);
        if (Number.isNaN(value)) {
            return;
        }

        value = Math.floor(value);

        setSaviorPresets((prev) => {
            const updated = prev.map((p) => {
                if (p.slot === saviorPresets[saviorPresetIndex].slot) {
                    const list = p.skillLevels;
                    list[skillIndex] = value;
                    return { ...p };
                }

                return p;
            });

            return updated;
        });
    };

    const updateSaviorPreset = () => {
        const updated = { ...saviorPresets[saviorPresetIndex] };
        let isUpdated = false;

        if (updated.level < 1) {
            updated.level = 1;
            isUpdated = true;
        }
        if (updated.favor < 1) {
            updated.favor = 1;
            isUpdated = true;
        }
        for (let i = 1; i < 4; i++) {
            if (updated.skillLevels[i] < 1) {
                updated.skillLevels[i] = 1;
                isUpdated = true;
            }
        }

        if (isUpdated) {
            setSaviorPresets((prev) => {
                const updatedList = prev.map((p) => {
                    if (p.slot === saviorPresets[saviorPresetIndex].slot) {
                        return updated;
                    }
                    return p;
                });

                return updatedList;
            });
        }

        updateCharPreset(updated);
    };

    const setVerifiedJourneyStat = (flag, textValue) => {
        let value = Number(textValue);
        if (Number.isNaN(value)) {
            return;
        }

        value = Math.floor(value);

        if (value > 1500) value = 1500;

        setArchivePresets((prev) => {
            const updated = prev.map((p) => {
                if (p.slot === archivePresets[archivePresetIndex].slot) {
                    p.stat[flag] = value;
                    return { ...p };
                }
                return p;
            });

            return updated;
        });
    };

    const addPotentialsFromSearchResult = (result) => {
        const potentials = result.resultType === "arcana" ? result.potentials : [result];

        let updated;
        setArchivePresets((prev) => {
            const updatedList = prev.map((p) => {
                if (p.slot === archivePresets[archivePresetIndex].slot) {
                    potentials.forEach((potential) => {
                        const found = p.potentials.find((e) => e.id === potential.id || e.id === potential.targetObj?.id);

                        if (!found) {
                            p.potentials.push({
                                id: potential.targetObj?.id ?? potential.id,
                                name: potential.targetObj?.name ?? potential.name,
                                level: 1,
                                maxLevel: potential.targetObj ? potential.targetObj.levels.length : potential.levels.length,
                            });
                        }
                    });

                    updated = { ...p };

                    updateArchivePreset(updated);

                    return updated;
                }

                return p;
            });

            return updatedList;
        });

        setArcanaQuery("");
    };

    const addPotential = (selected) => {
        if (selected.value === "잠재력 선택") return;

        const potential = potentialsOrigin.find((e) => e.id === selected.value);
        let updated;

        setArchivePresets((prev) => {
            const updatedList = prev.map((p) => {
                if (p.slot === archivePresets[archivePresetIndex].slot) {
                    const found = p.potentials.find((e) => e.id === potential.id);

                    if (!found) {
                        p.potentials.push({
                            id: potential.id,
                            name: potential.name,
                            level: 1,
                            maxLevel: potential.levels.length,
                        });
                        updated = { ...p };

                        return updated;
                    }
                }
                return p;
            });

            return updatedList;
        });

        updateArchivePreset(updated);
    };

    const removePotential = (id) => {
        setArchivePresets((prev) => {
            const updatedList = prev.map((p) => {
                if (p.slot === archivePresets[archivePresetIndex].slot) {
                    const found = p.potentials.find((e) => e.id === id);

                    if (found) {
                        const removed = p.potentials.filter((e) => e.id !== id);
                        const updated = { ...p, potentials: removed };

                        updateArchivePreset(updated);

                        return updated;
                    }
                }
                return p;
            });

            return updatedList;
        });
    };

    const setPotentialLevel = (id, textValue) => {
        let value = Number(textValue);
        if (Number.isNaN(value)) {
            return;
        }

        value = Math.floor(value);

        const potential = archivePresets[archivePresetIndex].potentials.find((e) => e.id === id);
        if (potential && potential.maxLevel < value) {
            value = potential.maxLevel;
        }

        setArchivePresets((prev) => {
            const updated = prev.map((p) => {
                if (p.slot === archivePresets[archivePresetIndex].slot) {
                    const found = p.potentials.find((e) => e.id === id);

                    if (found) {
                        found.level = value;
                        return { ...p };
                    }
                }
                return p;
            });

            return updated;
        });
    };

    const updateStellarArchivePreset = () => {
        const updated = { ...archivePresets[archivePresetIndex] };
        let isUpdated = false;

        for (let i = 0; i < updated.potentials.length; i++) {
            const potential = updated.potentials[i];

            if (potential.level > potential.maxLevel) {
                potential.level = potential.maxLevel;
                isUpdated = true;
            } else if (potential.level < 1) {
                isUpdated = true;
                potential.level = 1;
            }
        }

        if (isUpdated) {
            setArchivePresets((prev) => {
                const updatedList = prev.map((p) => {
                    if (p.slot === archivePresets[archivePresetIndex].slot) {
                        return updated;
                    }
                    return p;
                });

                return updatedList;
            });
        }

        updateArchivePreset(updated);
    };

    const setIsFold = (equipIndex, isFold) => {
        setEquipIsFold((prev) => {
            const updated = prev.map((p, i) => {
                if (i === equipIndex) {
                    return isFold;
                }
                return p;
            });

            return updated;
        });
    };

    const setEquipPart = (equip) => {
        setEquipPresets((prev) => {
            const prevClone = structuredClone(prev);

            const updated = prevClone.map((preset, index) => {
                if (index === equipPresetIndex) {
                    preset.equips = preset.equips.map((equipPart) => {
                        if (equipPart.part === equip.part) {
                            return equip;
                        }
                        return equipPart;
                    });

                    updateEquipPreset(preset);

                    return preset;
                }
                return preset;
            });

            return updated;
        });
    };

    const updateTacticToDB = (tactics) => {
        setTactics(tactics);
        updateTactics(tactics);
    };

    const updatePresets = async (saviorPreset, archivePreset, equipPreset) => {
        if (saviorPreset) {
            setSaviorPresets((prev) => {
                const updated = structuredClone(prev);

                return updated.map((e) => {
                    if (e.slot === saviorPreset.slot) return saviorPreset;

                    return e;
                });
            });
            await updateCharPreset(saviorPreset);
        }
        if (archivePreset) {
            setArchivePresets((prev) => {
                const updated = structuredClone(prev);

                return updated.map((e) => {
                    if (e.slot === archivePreset.slot) return archivePreset;

                    return e;
                });
            });
            await updateArchivePreset(archivePreset);
        }
        if (equipPreset) {
            setEquipPresets((prev) => {
                const updated = structuredClone(prev);

                return updated.map((e) => {
                    if (e.slot === equipPreset.slot) return equipPreset;

                    return e;
                });
            });
            await updateEquipPreset(equipPreset);
        }

        return true;
    };

    const downloadImage = async () => {
        setIsDownloading(true);

        try {
            const container = document.createElement("div");
            container.style.cssText = "position:fixed; left:-9999px; top:-9999px;";
            document.body.appendChild(container);

            const root = createRoot(container);
            const savior = saviors[selectedChar.value];
            await new Promise((resolve) => {
                root.render(
                    <ExtractImageTemplet
                        lang={lang}
                        savior={savior}
                        saviorPreset={saviorPresets[saviorPresetIndex]}
                        archivePreset={archivePresets[archivePresetIndex]}
                        equipPreset={equipPresets[equipPresetIndex]}
                        totalStat={statResult}
                        onReady={resolve}
                        potentialsOrigin={potentialsOrigin}
                        setOrigin={setOrigin}
                    />,
                );
            });

            const dataUrl = await toPng(container.firstChild, { backgroundColor: null });

            const link = document.createElement("a");
            link.download = `${savior.name[lang]}(${savior.title[lang]}).png`;
            link.href = dataUrl;
            link.click();

            root.unmount();
            document.body.removeChild(container);
        } catch (error) {
            console.log(error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div id="container" data-theme={theme} data-lang={lang}>
            {theme === "dark" && <StarContainer />}
            <Header title={getUIText(lang, "header.title.simulatorSavior")} />

            <div className={styles["savior-simulator-container"]}>
                {/* <div className={styles["savior-result-container"]}> */}
                <StatCollection lang={lang} result={statResult} />
                {/* </div> */}

                {/* left = savior settings */}
                <div className={styles["savior-settings-container"]}>
                    {/* left = savior-selector & base info & skills & archives */}
                    <div className={styles["savior-settings-left-container"]}>
                        {/* character selector - drop down */}
                        <FilterSelect
                            id={"savior_simulator_select_savior"}
                            isUseDispatch={false}
                            list={charactorNameFilterList}
                            selected={selectedChar}
                            changeFilter={setSelectedChar}
                            forceWidth={"100%"}
                        />

                        {/* <button type="button" className={styles["preset-export-button"]} onClick={() => setIsActiveExportModal(true)}>
                            {getUIText(lang, "simulator.savior.buttons.export.preset")}
                        </button> */}
                        <button
                            type="button"
                            className={styles["preset-export-button"]}
                            onClick={() => downloadImage()}
                            disabled={isDownloading}
                        >
                            {isDownloading
                                ? getUIText(lang, "simulator.savior.buttons.downloading")
                                : getUIText(lang, "simulator.savior.buttons.download")}
                        </button>
                        <div className={styles["savior-settings-base-container"]}>
                            <PresetChanger
                                presets={saviorPresets}
                                selectedIndex={saviorPresetIndex}
                                setSelectedIndex={setSaviorPresetIndex}
                                changePresetName={(name) => setPresetName("savior", name)}
                                key="savior_preset_changer"
                            />
                            <div className={styles["status-container"]}>
                                <Portrait savior={saviors[selectedChar.value]} isSmall={true} />
                                <div className={styles["base-settings"]}>
                                    <div className={styles["base-setting"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.savior.title.level")}</div>
                                        <input
                                            type="text"
                                            className={styles["base-value"]}
                                            value={saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].level : 0}
                                            onChange={(e) => setVerifiedSaviorValue("level", e.target.value)}
                                            onBlur={updateSaviorPreset}
                                        />
                                    </div>
                                    <div className={styles["base-setting"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.savior.title.resonance")}</div>
                                        <input
                                            type="text"
                                            className={styles["base-value"]}
                                            value={saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].resonance : 0}
                                            onChange={(e) => setVerifiedSaviorValue("resonance", e.target.value)}
                                            onBlur={updateSaviorPreset}
                                        />
                                    </div>
                                    <div className={styles["base-setting"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.savior.title.limitBreak")}</div>
                                        <input
                                            type="text"
                                            className={styles["base-value"]}
                                            value={saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].limitBreak : 0}
                                            onChange={(e) => setVerifiedSaviorValue("limitBreak", e.target.value)}
                                            onBlur={updateSaviorPreset}
                                        />
                                    </div>
                                    <div className={styles["base-setting"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.savior.title.starGrade")}</div>
                                        <input
                                            type="text"
                                            className={styles["base-value"]}
                                            value={saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].starGrade : 0}
                                            onChange={(e) => setVerifiedSaviorValue("starGrade", e.target.value)}
                                            onBlur={updateSaviorPreset}
                                        />
                                    </div>
                                    <div className={styles["base-setting"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.savior.title.affiliation")}</div>
                                        <input
                                            type="text"
                                            className={styles["base-value"]}
                                            value={saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].favor : 0}
                                            onChange={(e) => setVerifiedSaviorValue("favor", e.target.value)}
                                            onBlur={updateSaviorPreset}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles["skills"]}>
                                {saviors[selectedChar.value].skills.map((skill, skillIndex) => {
                                    const attr = SAVIOR_ATTRIBUTE[saviors[selectedChar.value].attr]["en-US"].toLowerCase();
                                    return (
                                        <div className={`${styles["skill"]} ${styles[attr]}`} key={`savior_skill_${skill.id}`}>
                                            <div className={styles["icon"]}>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/icon/skills/${selectedChar.img}_${SKILL_TYPE[skill.type][LOCALES.KO]}.webp`}
                                                />
                                            </div>
                                            <div className={styles["skill-type"]}>{SKILL_TYPE[skill.type][[lang]]}</div>
                                            {skill.type === 0 ? (
                                                <div className={styles["passive"]}>
                                                    {(saviorPresets[saviorPresetIndex] ? saviorPresets[saviorPresetIndex].limitBreak : 0) +
                                                        1}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className={styles["base-value"]}
                                                    value={
                                                        saviorPresets[saviorPresetIndex]
                                                            ? saviorPresets[saviorPresetIndex].skillLevels[skillIndex]
                                                            : 1
                                                    }
                                                    onChange={(e) => setVerifiedSkillValue(skillIndex, e.target.value)}
                                                    onBlur={updateSaviorPreset}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {tactics && (
                            <div className={`${styles["equipment-wrapper"]} ${tacticsIsFold ? `` : `${styles["active"]}`}`}>
                                <button
                                    type="button"
                                    className={styles["equip-fold-button"]}
                                    onClick={() => setTacticsIsFold(!tacticsIsFold)}
                                >
                                    {getUIText(lang, "simulator.title.tactics")} <span className={styles["triangle"]}>▼</span>
                                </button>

                                <div className={styles["tactics-settings"]} onClick={() => setEditingTactics(structuredClone(tactics))}>
                                    <span>
                                        {getUIText(lang, "simulator.savior.tactics.facility.title")} : {tactics.facility}
                                    </span>
                                    <span>
                                        {getUIText(lang, "simulator.savior.tactics.savior.title")} : {tactics.savior}
                                    </span>
                                    <span>
                                        {getUIText(lang, "simulator.savior.tactics.arcana.title")} : {tactics.arcana.level}
                                    </span>
                                    <table>
                                        <tbody>
                                            <tr>
                                                {/* <td>속성</td> */}
                                                <td>{getUIText(lang, "simulator.savior.tactics.attr.sun.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.attr.moon.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.attr.star.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.attr.order.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.attr.chaos.title")}</td>
                                                <td></td>
                                            </tr>
                                            <tr>
                                                {/* <td>레벨</td> */}
                                                <td>{tactics.attr.sun.level}</td>
                                                <td>{tactics.attr.moon.level}</td>
                                                <td>{tactics.attr.star.level}</td>
                                                <td>{tactics.attr.order.level}</td>
                                                <td>{tactics.attr.chaos.level}</td>
                                                <td></td>
                                            </tr>
                                            <tr>
                                                {/* <td>클래스</td> */}
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.defender.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.striker.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.ranger.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.caster.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.assassin.title")}</td>
                                                <td>{getUIText(lang, "simulator.savior.tactics.class.supporter.title")}</td>
                                            </tr>
                                            <tr>
                                                {/* <td>레벨</td> */}
                                                <td>{tactics.class.defender.level}</td>
                                                <td>{tactics.class.striker.level}</td>
                                                <td>{tactics.class.ranger.level}</td>
                                                <td>{tactics.class.caster.level}</td>
                                                <td>{tactics.class.assassin.level}</td>
                                                <td>{tactics.class.supporter.level}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles["savior-settings-right-container"]}>
                        <div className={styles["archive-settings"]}>
                            <PresetChanger
                                presets={archivePresets}
                                selectedIndex={archivePresetIndex}
                                setSelectedIndex={setArchivePresetIndex}
                                changePresetName={(name) => setPresetName("archive", name)}
                                key="archive_preset_changer"
                            />
                            <table>
                                <tbody>
                                    <tr className={styles["table-border"]}>
                                        <td className={styles["icon"]}>
                                            <img src={`${process.env.PUBLIC_URL}/images/icon/journey_status/힘.webp`} />
                                        </td>
                                        <td className={styles["icon"]}>
                                            <img src={`${process.env.PUBLIC_URL}/images/icon/journey_status/체력.webp`} />
                                        </td>
                                        <td className={styles["icon"]}>
                                            <img src={`${process.env.PUBLIC_URL}/images/icon/journey_status/인내.webp`} />
                                        </td>
                                        <td className={styles["icon"]}>
                                            <img src={`${process.env.PUBLIC_URL}/images/icon/journey_status/집중.webp`} />
                                        </td>
                                        <td className={styles["icon"]}>
                                            <img src={`${process.env.PUBLIC_URL}/images/icon/journey_status/보호.webp`} />
                                        </td>
                                    </tr>
                                    <tr className={styles["table-border"]}>
                                        <td>
                                            <input
                                                type="text"
                                                className={styles["base-value"]}
                                                value={archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].stat.str : 0}
                                                onChange={(e) => setVerifiedJourneyStat("str", e.target.value)}
                                                onBlur={updateStellarArchivePreset}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className={styles["base-value"]}
                                                value={archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].stat.hp : 0}
                                                onChange={(e) => setVerifiedJourneyStat("hp", e.target.value)}
                                                onBlur={updateStellarArchivePreset}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className={styles["base-value"]}
                                                value={archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].stat.end : 0}
                                                onChange={(e) => setVerifiedJourneyStat("end", e.target.value)}
                                                onBlur={updateStellarArchivePreset}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className={styles["base-value"]}
                                                value={
                                                    archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].stat.focus : 0
                                                }
                                                onChange={(e) => setVerifiedJourneyStat("focus", e.target.value)}
                                                onBlur={updateStellarArchivePreset}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className={styles["base-value"]}
                                                value={
                                                    archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].stat.prot : 0
                                                }
                                                onChange={(e) => setVerifiedJourneyStat("prot", e.target.value)}
                                                onBlur={updateStellarArchivePreset}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={5} className={styles["potential-selector"]}>
                                            <div className={styles["blank"]}></div>
                                            <FilterSelect
                                                id={"potential_selector"}
                                                list={potentialNameFilterList}
                                                selected={potentialNameFilterList[0]}
                                                changeFilter={addPotential}
                                                forceWidth={"100%"}
                                                isUseDispatch={false}
                                            />
                                            <div className={styles["blank"]}></div>
                                            <SearchBox
                                                query={arcanaQuery}
                                                changeQuery={setArcanaQuery}
                                                placeholder={"잠재력, 아르카나, 구원자명 입력"}
                                            />
                                            {
                                                <div
                                                    className={`${styles["search-results"]} ${arcanaQuery !== "" ? styles["active"] : ""}`}
                                                >
                                                    {resultList.length === 0 && <div>검색 결과가 없습니다.</div>}
                                                    {resultList.length > 0 &&
                                                        resultList.map((e) => {
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className={`${styles["search-result"]} ${styles[e.resultType]}`}
                                                                    onClick={() => addPotentialsFromSearchResult(e)}
                                                                >
                                                                    <div className={styles["name-wrapper"]}>
                                                                        <div className={styles["type"]}>
                                                                            {e.resultType === "arcana" ? "아르카나" : "잠재력"}
                                                                        </div>
                                                                        <div className={styles["name"]}>
                                                                            {e.resultType === "arcana"
                                                                                ? `- ${e.name[lang]}(${e.charName[lang]})`
                                                                                : `[${e.name[lang]}]`}
                                                                        </div>
                                                                    </div>
                                                                    {e.resultType === "arcana" &&
                                                                        e.potentials.map((p) => `[${p.targetObj.name[lang]}]`).join(" ")}
                                                                </button>
                                                            );
                                                        })}
                                                </div>
                                            }
                                        </td>
                                    </tr>
                                    {(archivePresets[archivePresetIndex] ? archivePresets[archivePresetIndex].potentials : []).map(
                                        (potential) => {
                                            return (
                                                <tr className={styles["potential"]} key={potential.id}>
                                                    <td>
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/images/icon/potential/${getImageFileName(potential.name[LOCALES.KO])}.webp`}
                                                        />
                                                    </td>
                                                    <td colSpan={2}>{potential.name[lang]}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className={styles["base-value"]}
                                                            value={potential.level}
                                                            onChange={(e) => setPotentialLevel(potential.id, e.target.value)}
                                                            onBlur={updateStellarArchivePreset}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="close-button"
                                                            style={{
                                                                position: "initial",
                                                                width: "20px",
                                                                height: "20px",
                                                                fontSize: "10px",
                                                                zIndex: 0,
                                                            }}
                                                            onClick={() => removePotential(potential.id)}
                                                        >
                                                            <i className="fi fi-sr-x" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        },
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* right = equipments */}
                        <div className={styles["savior-settings-equipments-container"]}>
                            {/* equipment preset */}
                            <PresetChanger
                                presets={equipPresets}
                                selectedIndex={equipPresetIndex}
                                setSelectedIndex={setEquipPresetIndex}
                                changePresetName={(name) => setPresetName("equip", name)}
                                key="equip_preset_changer"
                            />
                            <div className={styles["equipment-settings"]}>
                                <div className={styles["equipment"]}>
                                    <div className={styles["equipment-info"]}>
                                        <div className={styles["title"]}>{getUIText(lang, "simulator.title.equip")}</div>
                                        {Object.keys(STAT_TYPE).map((statType) => {
                                            const int = totalEquipStats.options.find(
                                                (e) => e.statType === statType && e.isPercent === false,
                                            ) || { value: 0 };
                                            const percent = totalEquipStats.options.find(
                                                (e) => e.statType === statType && e.isPercent === true,
                                            ) || { value: 0 };

                                            if (int.value === 0 && percent.value === 0) return;

                                            return (
                                                <div className={styles["equipment-stat-wrapper"]} key={`equip_total_stat_${statType}`}>
                                                    <div className={styles["equipment-stat"]}>
                                                        <div className={styles["stat-name"]}>{NORMAL_STATUS_TYPE[statType][lang]}</div>
                                                        <div className={styles["stat-value"]}>
                                                            {int.value !== 0 && `(+${int.value})`}{" "}
                                                            {percent.value !== 0 && `(+${percent.value / 100}%)`}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div className={styles["active-sets"]}>
                                            {totalEquipStats.sets.map((set, index) => {
                                                return (
                                                    <div
                                                        className={`${styles["equipment-set"]} ${set.parts === set.need_parts ? `${styles["active"]}` : ""}`}
                                                        key={`equip_active_set_${set.id}_${index}`}
                                                    >
                                                        <img src={`${process.env.PUBLIC_URL}/images/icon/equipments/${set.img}`} />
                                                        <span>
                                                            {set.name[lang]} (
                                                            {set.stats
                                                                .map(
                                                                    (stat) =>
                                                                        `${NORMAL_STATUS_TYPE[stat.type][lang]} ${stat.is_percent ? `${stat.value / 100}%` : stat.value}`,
                                                                )
                                                                .join("/")}
                                                            ) ({set.parts}/{set.need_parts})
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                {(equipPresets[equipPresetIndex] ? equipPresets[equipPresetIndex].equips : []).map((equip, equipIndex) => {
                                    const set = setOrigin.find((e) => e.id === equip.setId);

                                    return (
                                        <div
                                            className={`${styles["equipment-wrapper"]} ${equipIsFold[equipIndex] ? `` : `${styles["active"]}`}`}
                                            key={`equip_part_${equip.part}`}
                                        >
                                            <button
                                                type="button"
                                                className={styles["equip-fold-button"]}
                                                onClick={() => setIsFold(equipIndex, !equipIsFold[equipIndex])}
                                            >
                                                {EQUIP_PART_TYPE[equip.part][lang]} <span className={styles["triangle"]}>▼</span>
                                            </button>
                                            <div
                                                className={`${styles["equipment"]} ${equip.isEngraved ? `${styles["engraved"]}` : ""} `}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    if (equip.name !== "") {
                                                        setEditingEquip(equip);
                                                    }
                                                }}
                                            >
                                                <div className={styles["equipment-summary-wrapper"]}>
                                                    {equip.name !== "" && (
                                                        <div
                                                            className={`${styles["equip-image"]} ${equip.grade === 2 ? `${styles["unique"]}` : `${styles["legendary"]}`}`}
                                                        >
                                                            <img src={`${process.env.PUBLIC_URL}/images/icon/equipments/${equip.img}`} />
                                                            {equip.isEngraved && (
                                                                <img
                                                                    className={styles["frame"]}
                                                                    src={`${process.env.PUBLIC_URL}/images/icon/equipments/engraved.webp`}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {equip.name === "" && (
                                                        <div className={styles["equip-image"]}>{EQUIP_PART_TYPE[equip.part][lang]}</div>
                                                    )}
                                                    <div className={styles["equipment-summary"]}>
                                                        <div className={styles["equipment-name-wrapper"]}>
                                                            <div className={styles["equipment-name"]}>
                                                                {equip.name !== ""
                                                                    ? `${equip.name[lang]} +${equip.enhanced}`
                                                                    : getUIText(lang, "simulator.savior.equip.each.basic.title")}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className={styles["equipment-reset-button"]}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                            >
                                                                <i className="fi fi-rr-rotate-left" />
                                                            </button>
                                                        </div>
                                                        {equip.name !== "" && (
                                                            <div className={styles["equipment-set"]}>
                                                                <img src={`${process.env.PUBLIC_URL}/images/icon/equipments/${set.img}`} />
                                                                <span>{set.name[lang]}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {equip.name !== "" && (
                                                    <div className={styles["equipment-info"]}>
                                                        <div className={styles["equipment-stat-wrapper"]}>
                                                            <div className={styles["equipment-stat"]}>
                                                                <div className={styles["stat-name"]}>
                                                                    {NORMAL_STATUS_TYPE[equip.mainOption.statType][lang]}
                                                                </div>
                                                                <div className={styles["stat-value"]}>
                                                                    {equip.mainOption.isPercent
                                                                        ? `+${(equip.mainOption.baseValue + equip.mainOption.gap * (equip.enhanced + (equip.isEngraved ? 3 : 0))) / 100}%`
                                                                        : ""}
                                                                    {!equip.mainOption.isPercent
                                                                        ? `+${equip.mainOption.baseValue + equip.mainOption.gap * (equip.enhanced + (equip.isEngraved ? 3 : 0))}`
                                                                        : ""}
                                                                </div>
                                                            </div>
                                                            <hr className={styles["stat-divider"]} />
                                                        </div>
                                                        {Object.keys(STAT_TYPE).map((statType) => {
                                                            const intList = equip.subOptions.filter(
                                                                (e) => e.statType === statType && e.isPercent === false,
                                                            );
                                                            const percentList = equip.subOptions.filter(
                                                                (e) => e.statType === statType && e.isPercent === true,
                                                            );

                                                            let int = 0;
                                                            intList.forEach((e) => (int += e.value));

                                                            let percent = 0;
                                                            percentList.forEach((e) => (percent += e.value));

                                                            if (int === 0 && percent === 0) return;

                                                            return (
                                                                <div
                                                                    className={styles["equipment-stat-wrapper"]}
                                                                    key={`equip_part_${equip.part}_stat_${statType}`}
                                                                >
                                                                    <div className={styles["equipment-stat"]}>
                                                                        <div className={styles["stat-name"]}>
                                                                            {NORMAL_STATUS_TYPE[statType][lang]}
                                                                        </div>
                                                                        <div className={styles["stat-value"]}>
                                                                            {int !== 0 && `(+${int})`}{" "}
                                                                            {percent !== 0 && `(+${percent / 100}%)`}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* <div className={styles["savior-result-container"]}>
                    left = stat collection
                    <StatCollection lang={lang} result={statResult} />

                    right = comparison-group - col
                    <ComparisonGroup
                        lang={lang}
                        savior={saviors[selectedChar.value]}
                        saviorPreset={saviorPresets[saviorPresetIndex]}
                        statResult={statResult}
                    />
                </div> */}
            </div>

            {/* <PresetExportModal
                lang={lang}
                isActive={isActiveExportModal}
                setIsActive={setIsActiveExportModal}
                saviorPresets={saviorPresets}
                archivePresets={archivePresets}
                equipPresets={equipPresets}
                setPresets={updatePresets}
            /> */}
            <TacticsSelectorModal
                lang={lang}
                editingTactics={editingTactics}
                setEditingTactics={setEditingTactics}
                setTactics={updateTacticToDB}
            />
            <EquipSelectorModal lang={lang} editingEquip={editingEquip} setEditingEquip={setEditingEquip} setEquip={setEquipPart} />

            <div id="portal-root" />
            <ScrollUpButton />
            <Footer />
        </div>
    );
}
